import type { ProgrammaticLandingPageConfig } from '../types'
import {
  STORAGE_REFERENCE_KWP,
  SELF_CONSUMPTION_WITHOUT_BATTERY,
  SELF_CONSUMPTION_BATTERY_BONUS,
  SELF_CONSUMPTION_WITH_BATTERY,
  SELF_CONSUMPTION_CAP,
  BATTERY_COST_PER_KWH_RANGE_EUR,
} from './storageSizeFacts'

export const KWH_20_SPEICHER_KOSTEN: ProgrammaticLandingPageConfig = {
  slug: '20-kwh-speicher-kosten',
  lang: 'de',
  topicId: 'kwh-speicher-20',
  title: '20 kWh Batteriespeicher: Kosten & Richtwerte 2026',
  metaDescription:
    'Was kostet ein 20-kWh-Batteriespeicher? Für wen sich diese Speichergröße eignet, ' +
    'Eigenverbrauchs-Richtwerte und kostenloser Preisvergleich für Anlage + Speicher.',
  keywords: [
    '20 kWh Speicher Kosten',
    'Batteriespeicher 20 kWh Preis',
    '20 kWh Solarspeicher',
    'Was kostet ein 20 kWh Speicher',
  ],
  eyebrow: 'Speichergröße 20 kWh',
  h1: '20 kWh Batteriespeicher: Kosten und Richtwerte',
  intro:
    'Ein 20-kWh-Speicher ist unsere größte indexierte Speicherstufe - gedacht für große ' +
    'Haushalte mit Wärmepumpe und E-Auto gleichzeitig, kombiniert mit einer ' +
    `${STORAGE_REFERENCE_KWP}-kWp-Referenzanlage. Hier finden Sie Richtwerte zur passenden ` +
    'Haushaltsgröße, zum Eigenverbrauch mit Speicher und direkt darunter einen Rechner mit dem ' +
    'für diese Kombination vorbelegten Durchschnittspreis aus echten Handwerker-Angeboten.',
  calculatorPrefill: { kind: 'kwp+storage', kwp: STORAGE_REFERENCE_KWP, kwh: 20 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Für wen eignet sich ein 20-kWh-Speicher?' },
    {
      type: 'paragraph',
      text: 'Richtwert: 20 kWh eignen sich für große Haushalte mit Wärmepumpe UND E-Auto und hohem ' +
        'angestrebtem Autarkiegrad. Bei einer größeren ' +
        `Anlage als ${STORAGE_REFERENCE_KWP} kWp steigt in der Regel auch der sinnvoll nutzbare ` +
        'Speicheranteil - eine genaue Empfehlung für Ihr Dach liefert der PV-Rechner.',
    },
    { type: 'heading', text: 'Eigenverbrauch mit Batteriespeicher' },
    {
      type: 'paragraph',
      text:
        `Ohne Speicher liegt der Eigenverbrauchsanteil bei einer ${STORAGE_REFERENCE_KWP}-kWp-Anlage ` +
        `typischerweise bei rund ${SELF_CONSUMPTION_WITHOUT_BATTERY * 100} %. Mit Batteriespeicher ` +
        `steigt dieser Anteil im Rechenmodell um ${SELF_CONSUMPTION_BATTERY_BONUS * 100} ` +
        `Prozentpunkte auf rund ${SELF_CONSUMPTION_WITH_BATTERY * 100} %, mit Wärmepumpe und E-Auto ` +
        `zusammen bis zur Obergrenze von ${SELF_CONSUMPTION_CAP * 100} % - ab diesem Punkt bringt ` +
        'ein noch größerer Speicher rechnerisch kaum weiteren Nutzen. Eine genaue, auf Ihren ' +
        'Verbrauch zugeschnittene Berechnung liefert der PV-Rechner.',
    },
    { type: 'heading', text: 'Notstromfähigkeit' },
    {
      type: 'paragraph',
      text:
        'Mit 20 kWh lässt sich über eine Notstrom-Backup-Box in der Regel der überwiegende Teil ' +
        'eines Haushalts über einen längeren Zeitraum versorgen - wie lange genau, hängt vom ' +
        'tatsächlichen Verbrauch der angeschlossenen Geräte ab. Ob und in welchem Umfang ein ' +
        'Angebot Notstromfähigkeit einschließt, ist je Handwerker unterschiedlich - gezielt beim ' +
        'Angebot nachfragen.',
    },
    { type: 'heading', text: 'Speicherkosten und Förderung' },
    {
      type: 'paragraph',
      text:
        'Speicherkosten schwanken je nach Hersteller und Installationsaufwand stark - Marktrecherche ' +
        `2026 zeigt eine Spanne von etwa ${BATTERY_COST_PER_KWH_RANGE_EUR.min} bis ` +
        `${BATTERY_COST_PER_KWH_RANGE_EUR.max.toLocaleString('de-DE')} € pro kWh - bei 20 kWh ` +
        'entsprechend deutlich im Gesamtbudget spürbar, weshalb sich ein genauer Preisvergleich ' +
        'besonders lohnt. Speicherspezifische Förderungen gibt es regional unterschiedlich (z. B. ' +
        'Berliner Landesprogramm mit Zuschuss für Batteriespeicher) - Details im Förder-Überblick, ' +
        'eine genaue Berechnung für Ihre Kombination liefert der Rechner unten.',
    },
  ],
  faq: [
    {
      q: 'Für wen lohnt sich ein 20-kWh-Speicher?',
      a: 'Vor allem für große Haushalte mit Wärmepumpe und E-Auto gleichzeitig sowie hohem angestrebtem Autarkiegrad. Ohne diese Großverbraucher ist eine kleinere Stufe meist wirtschaftlicher.',
    },
    {
      q: 'Bringt ein noch größerer Speicher als 20 kWh weitere Vorteile?',
      a: 'Rechnerisch kaum - der Eigenverbrauchsanteil ist im Modell auf 85 % gedeckelt, unabhängig von der Speichergröße. Ab einem bestimmten Punkt steigen die Kosten schneller als der zusätzliche Nutzen.',
    },
    {
      q: 'Mit welcher Anlagengröße wird ein 20-kWh-Speicher meist kombiniert?',
      a: `Auf dieser Seite als Referenz eine ${STORAGE_REFERENCE_KWP}-kWp-Anlage, in der Praxis häufig auch mit 20 kWp oder mehr - eine genaue Empfehlung für Ihr Dach liefert der PV-Rechner.`,
    },
    {
      q: 'Reicht 20 kWh, um über Nacht komplett autark zu sein?',
      a: 'Ja, das reicht auch bei hohem Verbrauch (z. B. E-Auto-Ladung und Wärmepumpe zusammen) in aller Regel vollständig. Eine genaue, auf Ihren tatsächlichen Verbrauch zugeschnittene Einschätzung liefert der PV-Rechner.',
    },
  ],
  primaryCta: { label: 'Anlagengröße für mein Dach kostenlos berechnen', action: 'link', href: '/pv-rechner' },
  secondaryCta: { label: 'Direkt Anfrage erstellen', action: 'customer' },
  relatedLinks: [
    { label: '15 kWh Batteriespeicher: Kosten & Richtwerte', href: '/15-kwh-speicher-kosten' },
    { label: '10 kWh Batteriespeicher: Kosten & Richtwerte', href: '/10-kwh-speicher-kosten' },
    { label: '20 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/20-kwp-photovoltaik-kosten' },
    { label: 'Was kostet eine Solaranlage? Preise & Einflussfaktoren im Überblick', href: '/solaranlage-kosten' },
    { label: 'Photovoltaik-Förderung 2026: Deutschland & Österreich', href: '/blog/photovoltaik-foerderung-2026' },
    { label: 'PV-Kostenrechner: Durchschnittspreis für Ihre Anlage berechnen', href: '/pv-kostenrechner' },
  ],
}
